import { ShareAltOutlined, TableOutlined } from "@ant-design/icons";
import {
  AggregatedGraph,
  AggregatedNode,
} from "@p0security/shared/graph/aggregate";
import {
  DiscoverMatch,
  discoverPaths,
} from "@p0security/shared/graph/discover";
import {
  DslParseResult,
  isParseWarning,
} from "@p0security/shared/graph/dsl/index";
import { NodePredicate } from "@p0security/shared/graph/search";
import {
  DEFAULT_GRAPH_SEARCH_SETTINGS,
  DISCOVER_ONLY_DEFAULT_SETTINGS,
  GraphSearchSettings,
} from "@p0security/shared/graph/settings";
import { DirectedGraph } from "@p0security/shared/graph/types";
import { TIME_TO_DEBOUNCE, sleep } from "@p0security/shared/util/sleep";
import { Alert, Grid, Segmented, Spin } from "antd";
import { TableProps } from "antd/lib/table";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import {
  ResizableTable,
  ResizableTableColumnsType,
  ResizableTableKeys,
} from "components/ResizableTable";
import { Mask } from "components/common/Mask";
import { useLocalStorage } from "hooks/useLocalStorage";
import { compact, omit, uniq } from "lodash";
import {
  ReactElement,
  ReactNode,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import { staticError } from "utils/console";

import { useGuardedEffect } from "../../hooks/useGuardedEffect";
import {
  DiscoverVisualization,
  DiscoverVisualizationProps,
} from "./DiscoverVisualization";
import { GraphSearch, GraphSearchProps } from "./GraphSearch";
import {
  GraphSearchSettingsControl,
  GraphSearchSettingsDisables,
} from "./GraphSearchSettings";
import { LateralMovementToggle } from "./LateralMovementToggle";

export interface GraphTableProps<
  G extends object,
  K extends keyof G,
  A extends object,
>
  extends
    Omit<GraphSearchProps, "isSearching">,
    TableProps<AggregatedNode<G, K, A>> {
  aggregated: AggregatedGraph<G, A>;
  columns: ResizableTableColumnsType<AggregatedNode<G, K, A>>;
  exporter: (selected: AggregatedNode<G, K, A>[]) => ReactNode;
  from: NodePredicate<G>;
  onSearch?: (nodes: AggregatedNode<G, K, A>[]) => void;
  onSelection: (items: AggregatedNode<G, K, A>[]) => void;
  selectedNodes: AggregatedNode<G, K, A>[];
  search: DslParseResult<G>;
  settingsDisables?: GraphSearchSettingsDisables;
  visualize: Omit<DiscoverVisualizationProps<G>, "matches" | "settings">;
}

const GraphTableDisplayOptions = ({
  display,
  onChangeDisplay,
}: Pick<GraphSearchProps["controls"], "display" | "onChangeDisplay">) => {
  return (
    <Segmented
      value={display ?? "table"}
      options={[
        {
          value: "table",
          label: (
            <>
              <TableOutlined /> Table
            </>
          ),
        },
        {
          value: "graph",
          label: (
            <>
              <ShareAltOutlined /> Graph
            </>
          ),
        },
      ]}
      onChange={onChangeDisplay as any}
    />
  );
};

const TableWrapper = styled.div`
  .ant-table.ant-table-small {
    font-size: 13px;
    tr > td {
      padding: 6px 6px;
    }
  }
  .ant-pagination {
    font-size: 13px;
  }
  .ant-pagination-options .ant-select {
    font-size: 13px;
  }
`;

const GraphTableInternal = <
  G extends object,
  K extends keyof G,
  A extends object,
>({
  aggregated,
  columns,
  controls,
  exporter,
  extra,
  from,
  frozen,
  onSelection,
  selectedNodes,
  onSearch,
  search,
  settingsDisables,
  scopeNode,
  showOptions,
  visualize,
  ...tableProps
}: GraphTableProps<G, K, A>): ReactElement => {
  const [matches, setMatches] = useState<DiscoverMatch<DirectedGraph<G>>[]>();
  const { provider } = useContext(ScopeContext);

  const [settings, setSettings] = useLocalStorage<GraphSearchSettings>(
    "graph-search-settings",
    DEFAULT_GRAPH_SEARCH_SETTINGS
  );

  // Used to render a search in-progress indicator in the search bar;
  // note that we can't render the spinner in the table, as it would
  // clear table state (filtering, pagination, etc.)
  const [isSearching, setIsSearching] = useState(false);
  // Ensure that table display matches graph data structure by hiding
  // table when search has not yet updated
  const [renderShow, setRenderShow] = useState<string>();

  const nodeToStopOn = useCallback(
    (stopOn: boolean | undefined) =>
      stopOn !== undefined ? (stopOn ? ["lateral"] : []) : settings.stopOn,
    [settings.stopOn]
  );

  // "stopOn" graph search setting only applies on all targets graph.
  // On individual scope graphs, do not stop on any nodes.
  const pathSettings = useMemo(
    () =>
      controls.display === "graph"
        ? // Need +1 to display warning to user
          {
            ...settings,
            maxPaths: (settings.maxPaths ?? 0) + 1,
            stopOn:
              provider === "all" ? nodeToStopOn(settingsDisables?.stopOn) : [],
          }
        : {
            ...DISCOVER_ONLY_DEFAULT_SETTINGS,
            stopOn:
              provider === "all" ? nodeToStopOn(settingsDisables?.stopOn) : [],
          },
    [
      controls.display,
      nodeToStopOn,
      settings,
      settingsDisables?.stopOn,
      provider,
    ]
  );
  const pathDisables = useMemo(
    () => ({
      ...settingsDisables,
      ...(controls.display === "graph"
        ? {}
        : ({ maxPaths: true, maxResults: true } as const)),
    }),
    [controls.display, settingsDisables]
  );

  const parseWarnings = useMemo(
    () => (isParseWarning(search) ? [search.warning] : []),
    [search]
  );
  const warnings = useMemo(
    () => [
      ...uniq(parseWarnings),
      ...(isParseWarning(search)
        ? []
        : uniq(
            search.flatMap((s) =>
              compact([...s.pathTerms.map((pt) => pt.keytype)]).filter(
                (t) => t && pathSettings.stopOn.includes(t)
              )
            )
          ).map((s, ix) => (
            <div key={ix}>
              Query includes &apos;{s}:&apos;, but this search is disabled.
              Enable it above.
            </div>
          ))),
    ],
    [parseWarnings, pathSettings.stopOn, search]
  );

  useGuardedEffect(
    (cancellation) => async () => {
      // Clear selection on search / show update to prevent filtered rows from staying in
      // selection; this is less complicated than re-filtering the selection
      onSelection([]);
      setIsSearching(true);

      if (isParseWarning(search) || warnings.length) {
        setIsSearching(false);
        setMatches([]);
        return;
      }

      // Debounce state update
      await sleep(TIME_TO_DEBOUNCE);
      if (cancellation.isCancelled) return;
      const beforeSearch = performance.now();
      const result = await discoverPaths({
        cancellation,
        from,
        graph: aggregated,
        search,
        settings: pathSettings,
      });
      if (cancellation.isCancelled) return;
      // TODO: send to analytics
      // eslint-disable-next-line no-console
      console.log(
        "Time to search graph",
        (performance.now() - beforeSearch).toFixed(1),
        "ms"
      );
      setIsSearching(false);
      setMatches(result);
      setRenderShow(controls.show);
    },
    [
      aggregated,
      from,
      search,
      controls.show,
      onSearch,
      pathSettings,
      warnings.length,
      onSelection,
    ],
    staticError
  );

  const searched = useMemo(() => {
    // antd recursively flattens objects; it can take ant many seconds to flatten
    // just one page of top-level nodes; prevent this by removing the children altogether
    const noChildren = (matches?.map(({ node }) => omit(node, "children")) ??
      []) as AggregatedNode<G, K, A>[];
    return { nodes: noChildren };
  }, [matches]);

  useEffect(() => onSearch?.(searched.nodes), [onSearch, searched.nodes]);

  const registerSelection = useCallback(
    (_keys: React.Key[], values: AggregatedNode<G, K, A>[]) => {
      onSelection(values);
    },
    [onSelection]
  );

  const exporterElement = useMemo(
    () =>
      exporter(
        (selectedNodes.length ? selectedNodes : (searched?.nodes as any)) ?? []
      ),
    [exporter, searched, selectedNodes]
  );

  const selectedKeys = useMemo(
    () => selectedNodes.map((n) => n.key),
    [selectedNodes]
  );

  const breakpoints = Grid.useBreakpoint();
  const visualizationSize = useMemo(() => {
    const { md, lg, xl } = breakpoints;
    const height = "max(500px, calc(100vh - 90px))";
    if (xl) return { width: `calc(100vw - 260px)`, height };
    return {
      width: `calc(100vw - ${lg ? "250px" : md ? "130px" : "110px"})`,
      height,
    };
  }, [breakpoints]);

  const handleIncludeLateral = useCallback(
    (state: boolean) => {
      const currentStopOn =
        settings.stopOn ?? DEFAULT_GRAPH_SEARCH_SETTINGS.stopOn;
      setSettings({
        ...settings,
        stopOn: state
          ? currentStopOn.filter((v) => v !== "lateral")
          : [...currentStopOn, "lateral"],
      });
    },
    [setSettings, settings]
  );

  const lateralMovementDisabled = useMemo(
    () => settingsDisables?.stopOn !== undefined,
    [settingsDisables]
  );

  const lateralMovementChecked = useMemo(
    () =>
      settingsDisables?.stopOn !== undefined
        ? !settingsDisables.stopOn
        : !settings.stopOn?.includes("lateral"),
    [settingsDisables, settings]
  );

  const innerExtra = useMemo(
    () => ({
      ...(extra ?? {}),
      show: (
        <div
          style={{
            alignItems: "center",
            display: "inline-flex",
            gap: "0.4em",
          }}
        >
          {extra?.show}
          <GraphTableDisplayOptions
            display={controls.display}
            onChangeDisplay={controls.onChangeDisplay}
          />
          <GraphSearchSettingsControl
            disables={pathDisables}
            settings={settings}
            setSettings={setSettings}
          />
          {exporterElement}
        </div>
      ),
      toggle: (
        <LateralMovementToggle
          checked={lateralMovementChecked}
          disabled={lateralMovementDisabled}
          onChange={handleIncludeLateral}
        />
      ),
    }),
    [
      lateralMovementChecked,
      lateralMovementDisabled,
      handleIncludeLateral,
      controls.display,
      controls.onChangeDisplay,
      exporterElement,
      extra,
      pathDisables,
      setSettings,
      settings,
    ]
  );

  return (
    <div
      data-testid="graph-table"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1em",
        maxWidth: "1200px",
        minWidth: "600px",
      }}
    >
      {!searched ? (
        <div style={{ height: "430px" }}>
          <Spin />
        </div>
      ) : (
        <>
          <GraphSearch
            controls={controls}
            frozen={frozen}
            isSearching={isSearching}
            scopeNode={scopeNode}
            extra={innerExtra}
            showOptions={showOptions}
          />
          {warnings.length ? (
            <Alert
              data-testid="graph-table-warnings"
              style={{
                fontSize: "13px",
                fontFamily: '"Source Code Pro","Consolas",monospace',
                whiteSpace: "pre-wrap",
              }}
              type="warning"
              description={warnings.map((w, ix) => (
                <li key={ix}>{w}</li>
              ))}
            />
          ) : controls.show !== renderShow ? (
            <Spin />
          ) : (
            <Mask
              show={isSearching}
              // Ease mask on search so that quick searches don't flash the mask
              maskStyle={{
                transition: "background-color 0.1s 0.2s ease",
                width:
                  controls.display === "graph"
                    ? visualizationSize.width
                    : "100%",
                height:
                  controls.display === "graph"
                    ? visualizationSize.height
                    : "100%",
              }}
            >
              {controls.display === "graph" ? (
                isSearching ? (
                  <Spin />
                ) : (
                  <DiscoverVisualization<G>
                    matches={matches ?? []}
                    minimap
                    settings={settings}
                    {...visualize}
                    viewStyle={visualizationSize}
                  />
                )
              ) : (
                // Always fall back to table display
                <TableWrapper>
                  <ResizableTable
                    dataSource={
                      (searched?.nodes || []) as AggregatedNode<G, K, A>[]
                    }
                    columns={columns}
                    size="small"
                    expandable={{ showExpandColumn: false }}
                    tableId={ResizableTableKeys.graph}
                    data-testid="graph-table-inner-table"
                    rowSelection={{
                      onChange: registerSelection,
                      selectedRowKeys: selectedKeys,
                      type: "checkbox",
                      columnWidth: 15,
                    }}
                    // Sorter tooltips block other more important parts of the UI (like query reference)
                    showSorterTooltip={false}
                    {...{ pagination: { hideOnSinglePage: true } }}
                    {...tableProps}
                  />
                </TableWrapper>
              )}
            </Mask>
          )}
        </>
      )}
    </div>
  );
};

const MemoizedGraphTableInternal = memo(GraphTableInternal);
MemoizedGraphTableInternal.displayName = "GraphTable";
export const GraphTable =
  MemoizedGraphTableInternal as unknown as typeof GraphTableInternal; // Typecast because typescript is unable to properly handle the type parameters for GraphTableInternal
